import { createSelector } from 'reselect';
import { ContentType, Flags } from 'enums';
import appSelector, { intlAppSelector, editionSelector } from './appSelector';
import { pagePathWithPageId } from './pageSelector';
import selectedStreamSelector from './selectedStreamSelector';
import bootstrapSelector from './bootstrapSelector';
import { oneIdEntitlementsSelector } from './oneIdSelector';
import { adSiteSelector } from './adsSelector';
import {
    getAndroidVersionFromUserAgent,
    getPrerollConfigByEdition,
    getAdsConfigByEdition,
    getAdsConfigByAdProvider,
    hasTier
} from 'lib/util';

const playerSelector = ({ player = {} }) => player;

export default createSelector(playerSelector, (player) => player);

export const autoPlaySelector = createSelector(
    appSelector,
    selectedStreamSelector,
    ({ isMobile, location: { query: { autoplay } = {} } = {} }, selectedStream = {}) => {
        // set `autoplay` to false for the first mobile/tablet playback
        // until Brightcove fixes the issue where the player incorrectly detects the device is auto-playable.
        // TODO: remove this once Brightcove player fixes the mobile/tablet playback.
        if (isMobile) {
            return false;
        }

        if (autoplay != null) {
            return autoplay;
        }

        // The selectedStream used for playback does not get autoplayed if the tier property is not primary
        if (!hasTier(selectedStream, 'primary') && (selectedStream.content && selectedStream.content.type !== ContentType.VOD)) {
            return false;
        }

        return true;
    }
);

export const lastFeedsSelector = createSelector(
    playerSelector,
    ({ feeds }) => feeds
);

export const playerAdsConfigSelector = createSelector(
    adSiteSelector,
    pagePathWithPageId,
    bootstrapSelector,
    appSelector,
    editionSelector,
    (
        adSite,
        pathWithId,
        { syndicatedPlayer: { adsConfig = {} } = {} },
        { location: { query = {} } = {} },
        { edition = 'us' }
    ) => {
        const adProviderOverride = query[Flags.VIDEO_ADS_PROVIDER];
        const configByEdition = getAdsConfigByEdition(edition, adsConfig);
        let configByAdProvider = getAdsConfigByAdProvider(adProviderOverride, configByEdition);
        const isActiveAdProviderGoogle = (
            typeof configByAdProvider.activeAdProvider === 'string' &&
            configByAdProvider.activeAdProvider.toLowerCase().indexOf('google') !== -1
        );
        // NOTE: Appending `adUnit` only for Google Ad Manager
        if (adSite && isActiveAdProviderGoogle) {
            configByAdProvider = {
                ...configByAdProvider,
                ...{ adUnit: `${adSite}${pathWithId}` }
            };
        }

        if (isActiveAdProviderGoogle) {
            const cmsidOverride = query[Flags.VIDEO_ADS_CMSID];
            const networkCodeOverride = query[Flags.VIDEO_ADS_NETWORK_CODE];
            const adUnitOverride = query[Flags.VIDEO_ADS_AD_UNIT];

            configByAdProvider.cmsid = cmsidOverride || configByAdProvider.cmsid;
            configByAdProvider.networkCode = networkCodeOverride || configByAdProvider.networkCode;
            configByAdProvider.adUnit = adUnitOverride || configByAdProvider.adUnit;
        }

        return configByAdProvider;
    }
);

export const prerollConfigSelector = createSelector(
    bootstrapSelector,
    oneIdEntitlementsSelector,
    editionSelector,
    selectedStreamSelector,
    ({
        preRoll: { editions: prerollConfigEditionMap = {} } = {} },
        entitlements = [],
        { edition = 'us' },
        selectedStream = {}
    ) => {
        let defaultInterval = 0;
        let intervals = {};
        let exportedConfig = {};
        const { content = {} } = selectedStream;
        const contentType = content.type || null;
        const prerollEditionConfig = getPrerollConfigByEdition(edition, prerollConfigEditionMap);
        const prerollContentTypeConfig = contentType ? prerollEditionConfig[contentType] : null;

        if (prerollContentTypeConfig) {
            // Use defaults from contentType match
            intervals = prerollContentTypeConfig.intervals;
            if (intervals && intervals.default) {
                defaultInterval = intervals.default;
            }

            // Merge initial config with new contentType config
            exportedConfig.contentType = content.type;
            exportedConfig = { ...exportedConfig, ...prerollContentTypeConfig };
        }

        const rate = entitlements
            .map((packageName) => intervals[packageName])
            .reduce((minRate, packageRate = 1) => Math.min(minRate, packageRate), defaultInterval);

        // Add numerical rate and boolean flag to enable/disable preroll
        exportedConfig.calculatedRate = rate;
        exportedConfig.enablePreroll = (rate >= 1);

        return exportedConfig;
    }
);

export const recommendMobileWebSelector = createSelector(
    intlAppSelector,
    (intlApp) => intlApp && getAndroidVersionFromUserAgent() < 5.0
);

export const dssPlayerMutedSelector = createSelector(
    bootstrapSelector,
    ({ syndicatedPlayer: { dssPlayer: { muted } = {} } = {} }) => (muted)
);



// WEBPACK FOOTER //
// ./app/common/selectors/playerSelector.js